import type { Namespace } from '../types';

// Production planning (R-320). DE drafted — queued for review.
//
// The vocabulary this screen has to keep straight, because the whole model rests
// on it: a RACK holds TRAYS, a tray holds 4 SUB-TRAYS, and a sub-tray is the
// smallest thing a crop grows in. Quantities that cross a room boundary are
// stated in sub-trays; racks are stated per room.
export const productionPlanning = {
  // Shared verbs `common` does not carry under these exact names.
  save: { de: 'Speichern', en: 'Save' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  status: { de: 'Status', en: 'Status' },
  noSite: { de: 'Kein Standort gewählt', en: 'No site selected' },
  noSiteHint: {
    de: 'Planung gilt immer für einen Standort. Oben rechts einen auswählen.',
    en: 'Planning is always for one farm. Pick one from the site switcher above.',
  },
  committed: { de: 'Belegt', en: 'Committed' },
  freeSpace: { de: 'Frei', en: 'Free' },
  nextWeeks: { de: 'Nächste 8 Wochen', en: 'Next 8 weeks' },
  peakUse: { de: 'Racks · Spitze', en: 'Racks · peak' },
  farmHolds: { de: 'Sub-Trays · Kapazität', en: 'Sub-trays · capacity' },
  growingNow: { de: 'Läuft gerade', en: 'Growing now' },
  // `_one`/`_many` is the catalogue's plural convention — there is no ICU here.
  outsideWindow_one: {
    de: 'Ein weiterer Plan liegt außerhalb dieses Zeitraums.',
    en: 'One more plan falls outside this window.',
  },
  outsideWindow_many: {
    de: '{n} weitere Pläne liegen außerhalb dieses Zeitraums.',
    en: '{n} more plans fall outside this window.',
  },
  row: { de: 'Reihe', en: 'row' },
  configureFirst: {
    de: 'Zuerst Kapazität einrichten',
    en: 'Set up capacity first',
  },
  configureFirstHint: {
    de: 'Ein Plan braucht einen Raum mit Rack-Geometrie und eine Kultur mit hinterlegtem Ertrag. Beides unter Kapazität.',
    en: 'A plan needs a room with a rack geometry and a crop with a yield. Both live under Capacity.',
  },
  goToCapacity: { de: 'Zur Kapazität', en: 'Open Capacity' },
  dueToday: { de: 'Heute fällig', en: 'Due today' },
  dueThisWeek: { de: 'Diese Woche', en: 'This week' },
  dueLater: { de: 'Später', en: 'Later' },
  noDueDate: { de: 'Ohne Termin', en: 'No date' },
  overdue: { de: 'Überfällig', en: 'Overdue' },
  allStatuses: { de: 'Alle', en: 'All' },
  allStatusesPlaceholder: { de: 'Alle Status', en: 'All statuses' },
  // console R-357 — cancelled plans are history, not work. They stay out of the
  // default list and the count SAYS how many were withheld, so a hidden row is
  // never a row the operator thinks does not exist.
  showCancelled: { de: 'Stornierte zeigen', en: 'Show cancelled' },
  // console R-357 — the plan record's own Alerts tab.
  tabAlerts: { de: 'Alarme', en: 'Alerts' },
  // console R-357 — the plan record's Environment section.
  envTitle: { de: 'Umgebung', en: 'Environment' },
  envCropLabel: { de: 'Kultur', en: 'Crop' },
  envRoomLabel: { de: 'Raum', en: 'Room' },
  envRackLabel: { de: 'Regal', en: 'Rack' },
  envWindowLabel: { de: 'Zeitraum', en: 'Window' },
  envRoomLevel: { de: 'Raumebene', en: 'Room level' },
  envWindowIs: { de: '{from} — {to}', en: '{from} — {to}' },
  envInTarget: { de: 'Im Zielbereich', en: 'In target' },
  envWarning: { de: 'Warnung', en: 'Warning' },
  envCritical: { de: 'Kritisch', en: 'Critical' },
  envExpand: { de: 'Vergrößern', en: 'Expand' },
  envTarget: { de: 'Ziel {lo}–{hi} {unit}', en: 'Target {lo}–{hi} {unit}' },
  envOutOfTarget: {
    de: '{out}% außerhalb des Ziels · {crit}% kritisch',
    en: '{out}% out of target · {crit}% critical',
  },
  // ⚠ Nothing was JUDGED — not "everything was fine". The distinction matters:
  // a night during which a parameter is not judged is not a night it passed.
  envNotJudged: {
    de: 'In diesem Zeitraum nicht bewertet',
    en: 'Not judged in this window',
  },
  envCrop: { de: 'Kultur {n}', en: 'Crop {n}' },
  envWholeCycle: { de: 'Ganzer Zyklus', en: 'Whole cycle' },
  env24h: { de: '24 Std', en: '24 h' },
  env7d: { de: '7 Tage', en: '7 d' },
  envSelected: { de: 'Auswahl: {from} – {to}', en: 'Selected: {from} – {to}' },
  envClearSelection: { de: 'Auswahl aufheben', en: 'Clear selection' },
  // A drag zooms ONE chart. The WINDOW control is the shared data filter; these
  // are deliberately different words for deliberately different things.
  envResetZoom: { de: 'Zoom zurücksetzen', en: 'Reset zoom' },
  // console R-357 — the custom window. Presets first; this is tucked behind them.
  envCustom: { de: 'Benutzerdefiniert', en: 'Custom' },
  envLast: { de: 'Letzte', en: 'Last' },
  envBetween: { de: 'Zwischen', en: 'Between' },
  envAmount: { de: 'Anzahl', en: 'Amount' },
  envUnit: { de: 'Einheit', en: 'Unit' },
  envHours: { de: 'Stunden', en: 'Hours' },
  envDays: { de: 'Tage', en: 'Days' },
  envWeeks: { de: 'Wochen', en: 'Weeks' },
  envMonths: { de: 'Monate', en: 'Months' },
  envFrom: { de: 'Von', en: 'From' },
  envTo: { de: 'Bis', en: 'To' },
  envApply: { de: 'Anwenden', en: 'Apply' },
  // Refusals, stated as the fix rather than as a complaint.
  envBadAmount: { de: 'Anzahl muss größer als 0 sein', en: 'Amount must be more than 0' },
  envIncomplete: { de: 'Start und Ende wählen', en: 'Pick a start and an end' },
  envReversed: { de: 'Ende muss nach dem Start liegen', en: 'The end must be after the start' },
  // ⚠ Mirrors iot-history-local's 180-day MAX_RANGE_MS.
  envTooLong: { de: 'Höchstens 180 Tage', en: 'At most 180 days' },
  envDragToZoom: {
    de: 'Zum Zoomen im Diagramm ziehen — betrifft nur dieses Diagramm',
    en: 'Drag on the chart to zoom — affects this chart only',
  },
  envExport: { de: 'CSV exportieren', en: 'Export CSV' },
  // Console R-372 — the export panel.
  envExportDownload: { de: 'Herunterladen', en: 'Download' },
  envExportTime: { de: 'Zeitformat', en: 'Time format' },
  envExportTimeUtc: { de: 'Datum & Uhrzeit (UTC)', en: 'Date-time (UTC)' },
  envExportTimeMs: { de: 'Epoch-Millisekunden', en: 'Epoch milliseconds' },
  envExportTimeUtcHint: {
    de: 'Spalte time_utc, z. B. 2026-09-15 13:33:20 — Tabellenkalkulationen lesen das als Datum.',
    en: 'Column time_utc, e.g. 2026-09-15 13:33:20 — spreadsheets read it as a date.',
  },
  envExportTimeMsHint: {
    de: 'Spalte ts_ms, z. B. 1789479200000 — wie bisherige Exporte.',
    en: 'Column ts_ms, e.g. 1789479200000 — the same as earlier exports.',
  },
  envExportInterval: { de: 'Intervall', en: 'Interval' },
  envExportAuto: { de: 'Automatisch — wie im Diagramm', en: 'Automatic — as charted' },
  envExportRows: { de: '{label} · {rows} Zeilen', en: '{label} · {rows} rows' },
  envExportTooMany: {
    de: '{label} · {rows} Zeilen — über dem Limit',
    en: '{label} · {rows} rows — over the limit',
  },
  envExportIntervalHint: {
    de: 'Zeilen je Parameter und Ort. Ein Intervall über {cap} Zeilen für diesen Zeitraum ist nicht verfügbar — Zeitraum verkürzen, um es zu nutzen.',
    en: 'Rows per parameter and location. An interval over {cap} rows for this window is unavailable — shorten the window to use it.',
  },
  envExportIntervalTooMany: {
    de: 'Zu viele Zeilen für diesen Zeitraum — ein gröberes Intervall oder einen kürzeren Zeitraum wählen.',
    en: 'Too many rows for this window — choose a coarser interval or a shorter window.',
  },
  envExportWindow: { de: 'Zeitraum (UTC): {from} — {to}', en: 'Window (UTC): {from} — {to}' },
  envExportLoading: {
    de: 'Der neue Zeitraum lädt noch — gleich verfügbar.',
    en: 'The new window is still loading — available in a moment.',
  },
  envExportTruncated: {
    de: 'Der Export hat das Zeilenlimit erreicht und endet vor dem Ende des Zeitraums. Ein gröberes Intervall oder einen kürzeren Zeitraum wählen.',
    en: 'The export hit the row limit and stops before the end of the window. Choose a coarser interval or a shorter window.',
  },
  envIv1m: { de: '1 Minute', en: '1 minute' },
  envIv5m: { de: '5 Minuten', en: '5 minutes' },
  envIv15m: { de: '15 Minuten', en: '15 minutes' },
  envIv30m: { de: '30 Minuten', en: '30 minutes' },
  envIv1h: { de: '1 Stunde', en: '1 hour' },
  envIv6h: { de: '6 Stunden', en: '6 hours' },
  envIv12h: { de: '12 Stunden', en: '12 hours' },
  envIv1d: { de: '1 Tag', en: '1 day' },
  envRoom: { de: 'Raum {code}', en: 'Room {code}' },
  envRack: { de: 'Regal {code}', en: 'Rack {code}' },
  envLoop: { de: 'Wasserkreis {code}', en: 'Water loop {code}' },
  envDerived: { de: 'berechnet', en: 'derived' },
  envEstimated: { de: '~ geschätzt', en: '~ estimated' },
  envNoReadings: { de: 'Keine Messwerte in diesem Zeitraum', en: 'No readings in this window' },
  envNoBandNow: {
    de: 'Für diesen Abschnitt ist kein Grenzwert hinterlegt',
    en: 'No limit stated for this block',
  },
  envNoBoard: {
    de: 'Diesem Ort ist kein Controller zugeordnet — es gibt nichts zu messen.',
    en: 'No controller is assigned to this location — there is nothing to measure.',
  },
  envNoScopes: { de: 'Keine Räume aufgelöst', en: 'No rooms resolved' },
  envNoScopesHint: {
    de: 'Der Standort kennt für diese Kultur keinen Raum. Prüfen Sie die Zuteilung des Plans.',
    en: 'The site resolves no room for this crop. Check the plan\u2019s allocation.',
  },
  envNotStarted: { de: 'Noch nicht gestartet', en: 'Not started yet' },
  envNotStartedHint: {
    de: 'Der Plan ist noch nicht aktiviert, also gibt es keine Kultur, die etwas gemessen haben könnte.',
    en: 'This plan has not been activated, so there is no crop yet to have measured anything.',
  },
  // ⚠ Says the window is INCOMPLETE, not that the farm went quiet — the cut is
  // at the tail and the two look identical on a chart (backend pitfall #129).
  envTruncated: {
    de: 'Dieser Zeitraum wurde gekürzt und ist unvollständig — Zeitraum verkleinern oder gröbere Auflösung wählen.',
    en: 'This window was cut short and is incomplete — narrow the range or pick a coarser resolution.',
  },
  envDropped: {
    de: '{n} Messwerte verworfen: Einheit passt nicht zum Grenzwert.',
    en: '{n} readings dropped: their unit could not be reconciled with the band\u2019s.',
  },
  envUnmeasured: {
    de: 'Ohne Sensor auf dieser Anlage, daher nicht dargestellt: {list}.',
    en: 'No sensor on this platform reports these, so they are not charted: {list}.',
  },
  paramTemperature: { de: 'Temperatur', en: 'Temperature' },
  paramHumidity: { de: 'Luftfeuchte', en: 'Humidity' },
  paramCo2: { de: 'CO₂', en: 'CO₂' },
  paramPpfd: { de: 'PPFD', en: 'PPFD' },
  paramVpd: { de: 'VPD', en: 'VPD' },
  paramEc: { de: 'EC', en: 'EC' },
  paramPh: { de: 'pH', en: 'pH' },
  paramNutrientTemp: { de: 'Nährlösungstemperatur', en: 'Nutrient temperature' },
  planAlertsEmpty: { de: 'Keine Alarme', en: 'No alerts' },
  planAlertsEmptyHint: {
    de: '{code} hat bisher nichts gemeldet.',
    en: '{code} has reported nothing so far.',
  },
  planAlertsAllAcked: { de: 'Alles quittiert', en: 'All acknowledged' },
  planAlertsAllAckedHint: {
    de: 'Für diesen Plan ist kein Alarm mehr offen.',
    en: 'This plan has no open alerts left.',
  },
  cancelledHidden: { de: '{n} storniert ausgeblendet', en: '{n} cancelled hidden' },
  notes: { de: 'Notizen', en: 'Notes' },
  name: { de: 'Name', en: 'Name' },
  edit: { de: 'Bearbeiten', en: 'Edit' },
  delete: { de: 'Löschen', en: 'Delete' },
  loadFailed: { de: 'Laden fehlgeschlagen', en: 'Could not load' },
  pickCrop: { de: 'Kultur wählen…', en: 'Pick a crop…' },
  pickProfile: { de: 'Version wählen…', en: 'Choose a version…' },
  placeholderTargetKg: { de: 'z. B. 10', en: 'e.g. 10' },
  placeholderNotes: {
    de: 'Hinweise zu diesem Plan (optional)',
    en: 'Anything worth knowing about this plan (optional)',
  },
  pickRoom: { de: 'Raum wählen…', en: 'Pick a room…' },

  // ── capacity ───────────────────────────────────────────────────────────────
  capacityTitle: { de: 'Kapazität', en: 'Capacity' },
  capacityDesc: {
    de: 'Was ein Raum fassen kann und was eine Kultur je Sub-Tray liefert. Beides zusammen macht aus einem Gewicht eine Anzahl Racks.',
    en: 'What a room can hold and what a crop yields per sub-tray. Together they are what turns a weight into a number of racks.',
  },
  tabRooms: { de: 'Räume', en: 'Rooms' },
  tabYields: { de: 'Erträge', en: 'Crop yield' },

  room: { de: 'Raum', en: 'Room' },
  roomKind: { de: 'Art', en: 'Kind' },
  growRoom: { de: 'Grow Room', en: 'Grow room' },
  germinationRoom: { de: 'Keimraum', en: 'Germination room' },
  // Growing timeline, Stage-Gantt redesign — stage GROUPS (the API's stage codes, grouped).
  stageGroupGerm: { de: 'Keimung', en: 'Germinating' },
  stageGroupGrow: { de: 'Wachstum', en: 'Growing' },
  stageGroupFinish: { de: 'Reifung', en: 'Finishing' },
  stageGroupHarvest: { de: 'Ernte', en: 'Harvest' },
  stageGroupOther: { de: 'Sonstige Phase', en: 'Other stage' },
  legendStageDone: { de: 'Erledigt', en: 'Done' },
  legendCropInRoom: { de: 'Kultur im Raum', en: 'Crop in the room' },
  legendCleaning: { de: 'Reinigung', en: 'Cleaning' },
  tlDayOf: { de: 'Tag {n} von {total}', en: 'Day {n} of {total}' },
  tlStarts: { de: 'Beginnt {date}', en: 'Starts {date}' },
  tlEnded: { de: 'Beendet {date}', en: 'Ended {date}' },
  tlHarvestOn: { de: 'Ernte {date}', en: 'Harvest {date}' },
  tlEndsOn: { de: 'Endet {date}', en: 'Ends {date}' },
  tlRacks: { de: '{n} Regale', en: '{n} racks' },
  tlRacksUsed: { de: '{used}/{total} Regale', en: '{used}/{total} racks' },
  tlInUseUntil: { de: 'Belegt bis {date}', en: 'In use until {date}' },
  tlFreeNext: { de: 'Frei · nächste Kultur {date}', en: 'Free · next crop {date}' },
  tlFreeNow: { de: 'Jetzt frei', en: 'Free now' },
  tlFreeWindow: { de: 'Im ganzen Zeitraum frei', en: 'Free for the whole window' },
  tlRack: { de: 'Regal {n}', en: 'Rack {n}' },
  tlRackInUse: { de: 'belegt', en: 'in use' },
  tlRackFreeToday: { de: 'heute frei', en: 'free today' },
  tlRackGone: { de: 'nicht vorhanden', en: 'not on site' },
  tlExpandAll: { de: 'Alle aufklappen', en: 'Expand all' },
  tlCollapseAll: { de: 'Alle zuklappen', en: 'Collapse all' },
  tlShowRacks: { de: 'Regale von {room} zeigen', en: 'Show racks of {room}' },
  tlHideRacks: { de: 'Regale von {room} ausblenden', en: 'Hide racks of {room}' },
  tlCleaningTip: { de: 'Reinigung zwischen Kulturen', en: 'Cleaning between crops' },
  tlToday: { de: 'Heute', en: 'Today' },
  rackRowsTitle: { de: 'Rack-Reihen · {room}', en: 'Rack rows · {room}' },
  rackDay: { de: 'Tag', en: 'Day' },
  rackPlanted: { de: 'Belegt', en: 'Planted' },
  rackFree: { de: 'Frei', en: 'Free' },
  rackBeyondShort: { de: 'Nicht vorhanden', en: 'Not on site' },
  rackNone: { de: 'Dieser Raum hat keine Rack-Reihen', en: 'This room has no rack rows' },
  tlStageCount: { de: '{n} Phasen', en: '{n} stages' },
  envLast24h: { de: 'Letzte 24 Stunden', en: 'Last 24 hours' },
  envLast7d: { de: 'Letzte 7 Tage', en: 'Last 7 days' },
  envCustomRange: { de: 'Eigener Zeitraum', en: 'Custom range' },
  envCustomKind: { de: 'Art des Zeitraums', en: 'Kind of range' },
  envLastPeriod: { de: 'Letzte …', en: 'Last …' },
  envBetweenDates: { de: 'Zwischen zwei Daten', en: 'Between two dates' },
  envNoTarget: { de: 'Kein Ziel', en: 'No target' },
  planAlertsAllStates: { de: 'Alle Alarme', en: 'All alerts' },
  moveReasonPlaceholder: {
    de: 'z. B. Wurzelfäule auf Regal 2 – Kultur entfernt',
    en: 'e.g. Root rot found on rack 2 — crop pulled',
  },
  tlZoomWeek: { de: '6 Wochen', en: '6 weeks' },
  tlZoomMonth: { de: '3 Monate', en: '3 months' },
  tlZoomQuarter: { de: '6 Monate', en: '6 months' },
  tlPlansInWindow: { de: '{n} Pläne in diesem Zeitraum', en: '{n} plans in this window' },
  tlRoomsInUse: { de: '{n} Räume · {k} heute belegt', en: '{n} rooms · {k} in use today' },
  tlClickHint: { de: 'Auf einen Balken klicken, um den Plan zu öffnen', en: 'Click a bar to open its plan' },
  seedingRoom: { de: 'Aussaatraum', en: 'Seeding room' },
  racks: { de: 'Racks', en: 'Racks' },
  traysPerRack: { de: 'Trays je Rack', en: 'Trays per rack' },
  subtraysPerTray: { de: 'Sub-Trays je Tray', en: 'Sub-trays per tray' },
  changeoverDays: { de: 'Umrüsttage', en: 'Changeover days' },
  changeoverHint: {
    de: 'Tage, die ein Rack nach einer Kultur gereinigt wird, bevor es neu belegt werden darf.',
    en: 'Days a rack is cleaned down after a crop before anything may be planted in it.',
  },
  capacityTotal: { de: 'Fasst', en: 'Holds' },
  subtrays: { de: 'Sub-Trays', en: 'sub-trays' },
  observed: { de: 'Standort meldet', en: 'Site reports' },
  notConfigured: { de: 'Nicht konfiguriert', en: 'Not configured' },
  notConfiguredHint: {
    de: 'Dieser Raum ist dem Standort bekannt, aber ohne Rack-Geometrie. Bis dahin ist er nicht planbar.',
    en: 'The site knows this room but nobody has given it a rack geometry. It is not plannable until somebody does.',
  },
  disagrees: { de: 'Weicht vom Standort ab', en: 'Differs from the site' },
  disagreesHint: {
    de: 'Die konfigurierte Zahl gilt für die Planung. Der Standort meldet etwas anderes — kein Fehler, wenn ein Raum gebaut, aber noch nicht verkabelt ist.',
    en: 'The configured number is what planning uses. The site reports something else — not an error when a room is built but not yet wired.',
  },
  configureRoom: { de: 'Raum konfigurieren', en: 'Configure room' },
  noRooms: { de: 'Keine Anbauräume', en: 'No growing rooms' },
  noRoomsHint: {
    de: 'Dieser Standort meldet keinen Aussaat-, Keim- oder Grow-Raum. Räume entstehen im Standortbaum.',
    en: 'This site reports no seeding, germination or grow room. Rooms are created in the location tree.',
  },

  crop: { de: 'Kultur', en: 'Crop' },
  variety: { de: 'Sorte', en: 'Variety' },
  allVarieties: { de: 'Alle Sorten', en: 'All varieties' },
  gramsPerSubtray: { de: 'Gramm je Sub-Tray', en: 'Grams per sub-tray' },
  colActions: { de: 'Aktionen', en: 'Actions' },
  viewPlan: { de: 'Ansehen', en: 'View' },
  yieldMin: { de: 'Minimum', en: 'Minimum' },
  yieldMax: { de: 'Maximum', en: 'Maximum' },
  yieldMinPlaceholder: { de: 'z. B. 120', en: 'e.g. 120' },
  yieldMaxPlaceholder: { de: 'z. B. 160', en: 'e.g. 160' },
  yieldNotesPlaceholder: {
    de: 'z. B. Gemessen über die letzten drei Ernten in GR07',
    en: 'e.g. Measured over the last three harvests in GR07',
  },
  yieldHint: {
    de: 'Geplant wird mit dem MINIMUM — so wird die Zielmenge auch im schlechtesten Fall erreicht. Die Spanne wird überall mit angezeigt.',
    en: 'Planning sizes on the MINIMUM, so the target is met even in the worst case. The range is shown everywhere alongside.',
  },
  addYield: { de: 'Ertrag hinterlegen', en: 'Add a yield' },
  noYields: { de: 'Noch keine Erträge hinterlegt', en: 'No yields configured yet' },
  noYieldsHint: {
    de: 'Ohne Ertrag je Sub-Tray lässt sich ein Gewicht nicht in Racks umrechnen — eine Kultur ohne Eintrag ist nicht planbar.',
    en: 'Without a yield per sub-tray a weight cannot become racks, so a crop with no entry cannot be planned.',
  },

  // ── plans ──────────────────────────────────────────────────────────────────
  plansTitle: { de: 'Produktionspläne', en: 'Production plans' },
  plansDesc: {
    de: 'Aus einer Zielmenge werden Sub-Trays, Trays und Racks — und daraus Räume im Kalender.',
    en: 'A target weight, turned into sub-trays, trays and racks — and from there into rooms on a calendar.',
  },
  newPlan: { de: 'Neuer Plan', en: 'New plan' },
  planCode: { de: 'Plan', en: 'Plan' },
  target: { de: 'Ziel', en: 'Target' },
  expected: { de: 'Erwartet', en: 'Expected' },
  window: { de: 'Zeitraum', en: 'Window' },
  rooms: { de: 'Räume', en: 'Rooms' },
  openTasks: { de: 'Offene Aufgaben', en: 'Open tasks' },
  noPlans: { de: 'Noch keine Pläne', en: 'No plans yet' },
  noPlansHint: {
    de: 'Ein Plan beginnt mit einer Kultur und einer Zielmenge. Alles Weitere wird berechnet.',
    en: 'A plan starts with a crop and a target weight. Everything after that is computed.',
  },

  statusDraft: { de: 'Entwurf', en: 'Draft' },
  statusScheduled: { de: 'Eingeplant', en: 'Scheduled' },
  statusActive: { de: 'Läuft', en: 'Active' },
  statusCompleted: { de: 'Abgeschlossen', en: 'Completed' },
  statusCancelled: { de: 'Storniert', en: 'Cancelled' },
  statusFailed: { de: 'Gescheitert', en: 'Failed' },

  // ── the wizard ─────────────────────────────────────────────────────────────
  wizardStep1: { de: '1 · Was und wie viel', en: '1 · What and how much' },
  wizardStep2: { de: '2 · Wohin', en: '2 · Where it goes' },
  profile: { de: 'Rezeptversion', en: 'Recipe version' },
  profileHint: {
    de: 'Die Version wird festgeschrieben. Eine spätere Aktivierung ändert diesen Plan nicht.',
    en: 'The version is pinned. Activating a later one does not change this plan.',
  },
  targetWeight: { de: 'Zielmenge', en: 'Target weight' },
  startDate: { de: 'Startdatum', en: 'Start date' },
  // R-356 — a plan starts at a MINUTE, not on a day: the cycle counts from this
  // instant and every lamp edge in it is derived from the clock beside the date.
  startTime: { de: 'Startzeit', en: 'Start time' },
  startNow: { de: 'Jetzt starten', en: 'Start now' },
  startAtHint: {
    de: 'Datum und Uhrzeit — der Zyklus zählt ab diesem Zeitpunkt in Minuten.',
    en: 'A date and a clock — the cycle counts in minutes from this moment.',
  },
  // The short unit forms a duration is labelled with. Storage is always minutes;
  // the number picks the largest unit that expresses it whole.
  unitDayShort: { de: 'T', en: 'd' },
  unitHourShort: { de: 'Std', en: 'h' },
  unitMinuteShort: { de: 'Min', en: 'min' },
  // R-324: re-homed into step 2, where the number it overrides is on screen.
  subtrayOverride: {
    de: 'Sub-Trays anpassen (berechnet: {computed})',
    en: 'Adjust the sub-trays (computed: {computed})',
  },
  subtrayOverrideLabel: { de: 'Sub-Trays', en: 'Sub-trays' },
  subtrayOverrideHint: {
    de: 'Überschreibt die Ertragsrechnung. Trays und Racks werden weiterhin daraus abgeleitet.',
    en: 'Overrides the yield arithmetic. Trays and racks are still derived from it.',
  },
  sizing: { de: 'Aufteilung', en: 'Sizing' },
  // Lower-case twins of `racks` / `trays` / `subtrays`, which are COLUMN HEADERS
  // elsewhere and have to stay capitalised there. These read inside a sentence
  // of counts, and each has a singular form — "1 racks" is the kind of detail
  // that makes a screen look unfinished.
  racksLower: { de: 'Racks', en: 'racks' },
  rackOne: { de: 'Rack', en: 'rack' },
  trayOne: { de: 'Tray', en: 'tray' },
  subtrayOne: { de: 'Sub-Tray', en: 'sub-tray' },

  // Discarding a draft plan. Draft only, so these strings never have to explain
  // a refusal an operator can actually reach from the button.
  deletePlan: { de: 'Löschen', en: 'Delete' },
  deletePlanTitle: { de: 'Entwurf {code} löschen?', en: 'Delete draft {code}?' },
  deletePlanMsg: {
    de: 'Der Stufenplan, die Rack-Buchungen und die daraus erzeugten Aufgaben werden mitgelöscht. Das lässt sich nicht rückgängig machen.',
    en: "This draft's stage plan, its rack bookings and the tasks generated from it go with it. This cannot be undone.",
  },
  deletePlanConfirm: { de: 'Entwurf löschen', en: 'Delete draft' },
  planDeleted: { de: 'Entwurf {code} gelöscht', en: 'Draft {code} deleted' },
  trays: { de: 'Trays', en: 'trays' },
  cycleLength: { de: 'Zykluslänge', en: 'Cycle length' },
  days: { de: 'Tage', en: 'days' },
  phase: { de: 'Phase', en: 'Phase' },
  stages: { de: 'Phasen', en: 'Stages' },
  recommended: { de: 'Empfehlung', en: 'Recommended' },
  chooseRooms: { de: 'Räume wählen', en: 'Choose rooms' },
  useRecommendation: { de: 'Empfehlung übernehmen', en: 'Use the recommendation' },
  splitWarning: {
    de: 'Kein Raum fasst diese Phase allein — sie wird aufgeteilt. Das bedeutet zwei Klimazonen für ein Rezept und zusätzliche Wege.',
    en: 'No single room holds this phase, so it is split. That means two climates held to one recipe, and extra walking.',
  },
  doesNotFit: { de: 'Passt nicht', en: 'Does not fit' },
  free: { de: 'frei', en: 'free' },
  of: { de: 'von', en: 'of' },
  contiguousRun: { de: 'längster zusammenhängender Block', en: 'longest contiguous run' },
  createDraft: { de: 'Als Entwurf anlegen', en: 'Create draft' },
  fixCapacityFirst: {
    de: 'Mindestens eine Phase passt nicht. Ziel, Startdatum oder Räume anpassen.',
    en: 'At least one phase does not fit. Change the target, the start date or the rooms.',
  },

  // ── the record ─────────────────────────────────────────────────────────────
  overview: { de: 'Übersicht', en: 'Overview' },
  tabTimeline: { de: 'Zeitachse', en: 'Timeline' },
  tabAllocation: { de: 'Belegung', en: 'Allocation' },
  tabTasks: { de: 'Aufgaben', en: 'Tasks' },
  tabHistory: { de: 'Verlauf', en: 'History' },
  plannedStart: { de: 'Geplanter Start', en: 'Planned start' },
  actualStart: { de: 'Tatsächlicher Start', en: 'Actual start' },
  notRecorded: { de: 'Nicht erfasst', en: 'Not recorded' },
  markStarted: { de: 'Als gestartet erfassen', en: 'Record as started' },
  markEnded: { de: 'Als beendet erfassen', en: 'Record as ended' },

  // ── the record-a-stage confirmation (user, 2026-09-07) ─────────────────────
  //
  // Recording a stage used to fire on the click with no confirmation at all —
  // and it is not a small act: it is the write that re-anchors the room's light
  // and irrigation schedule, and it cannot be undone from this screen (the
  // server refuses a second recording rather than overwrite when the work
  // happened).
  recordStartTitle: { de: 'Phasenstart erfassen', en: 'Record this stage as started' },
  recordEndTitle: { de: 'Phasenende erfassen', en: 'Record this stage as ended' },
  recordWhat: {
    de: '{stage} — geplant für {date}.',
    en: '{stage} — planned for {date}.',
  },
  recordMovesTheRoom: {
    de: 'Damit wird der Zeitplan des Raums neu verankert: Licht und Bewässerung folgen dem, was erfasst wurde.',
    en: "This re-anchors the room's schedule — light and irrigation follow what is recorded, not the plan.",
  },
  recordNote: { de: 'Notiz (optional)', en: 'Note (optional)' },
  recordNoteHint: {
    de: 'Was aufgefallen ist. Wird an die Notizen der Phase angehängt, überschreibt nichts.',
    en: 'Anything you noticed. Appended to the stage’s notes — it overwrites nothing.',
  },
  recordConfirmStart: { de: 'Start erfassen', en: 'Record start' },
  recordConfirmEnd: { de: 'Ende erfassen', en: 'Record end' },
  // ⚠ R-356 — `{n}` now ARRIVES with its unit ("90 min", "1,5 Std", "2 T"),
  // because the variance is measured in minutes and a four-hour early start
  // used to render as "0 day(s) early". Never re-add the word "Tage" here.
  recordedEarly: {
    de: '{stage} erfasst — {n} FRÜHER als geplant. Der Raumplan verschiebt sich um {n} nach vorne.',
    en: '{stage} recorded — {n} EARLY. The room’s schedule moves {n} earlier.',
  },
  recordedLate: {
    de: '{stage} erfasst — {n} SPÄTER als geplant. Der Raumplan verschiebt sich um {n} nach hinten.',
    en: '{stage} recorded — {n} LATE. The room’s schedule moves {n} later.',
  },
  recordedOnTime: { de: '{stage} erfasst — planmäßig.', en: '{stage} recorded — on plan.' },
  // ⚠ The "whole days" sentence is RETIRED (R-356). It described a rule that no
  // longer exists: the room does not push an overlapping stage to the next day,
  // it starts it where the previous one ended — which is to say, now.
  recordedSameDay: {
    de: 'Hinweis: überschneidet die vorherige Phase — der Raum lässt sie ab sofort laufen.',
    en: 'Note: this overlaps the previous stage — the room will treat it as starting now.',
  },
  // ── the effective window (user, 2026-09-07: "why didn't it slide?") ───────
  //
  // It did slide — the ROOM re-anchored — but the only dates on screen were the
  // plan's promise and the recorded moments, so the drift was invisible.
  // A stage that was SKIPPED did not go unrecorded — it was closed out without
  // happening (a forced completion, or a failed plan). Saying "not recorded"
  // invites somebody to go and record it, which is exactly what the button used
  // to let them try.
  stageSkipped: { de: 'Übersprungen', en: 'Skipped' },
  stageOverdue: { de: 'Überfällig', en: 'Overdue' },

  colEffective: { de: 'Läuft tatsächlich', en: 'Actually running' },
  effectiveHint: {
    de: 'Was der Raum tatsächlich fährt: Licht und Bewässerung folgen dem Erfassten. Die Planwerte bleiben als Vergleichsbasis unverändert.',
    en: 'What the room is actually running — light and irrigation follow what was recorded. The planned dates stay put as the baseline.',
  },
  // `{n}` carries its own unit (R-356): `90 min`, `1,5 Std`, `2 T`.
  driftAhead: { de: '{n} früher', en: '{n} ahead' },
  driftBehind: { de: '{n} später', en: '{n} behind' },
  onPlan: { de: 'im Plan', en: 'on plan' },
  runningAhead: {
    de: 'Läuft {n} früher als geplant — Ernte {date} statt {planned}.',
    en: 'Running {n} ahead of plan — harvest {date}, not {planned}.',
  },
  runningBehind: {
    de: 'Läuft {n} später als geplant — Ernte {date} statt {planned}.',
    en: 'Running {n} behind plan — harvest {date}, not {planned}.',
  },
  legendEffective: { de: 'läuft tatsächlich', en: 'actually running' },
  legendPlanned: { de: 'geplant', en: 'planned' },

  recordWhen: { de: 'Wann (optional)', en: 'When (optional)' },
  recordWhenHint: {
    de: 'Leer = jetzt. Für Nacherfassung Datum und Uhrzeit setzen; ein Zeitpunkt in der Zukunft wird abgelehnt. Ohne Uhrzeit gilt 00:00.',
    en: 'Blank = now. Set the real date and time when catching up — a moment in the future is refused. No time means 00:00.',
  },
  recordWhenInvalid: { de: 'Ungültige Uhrzeit.', en: 'That is not a time.' },
  rackRows: { de: 'Rack-Reihen', en: 'Rack rows' },
  allocationPending: {
    de: 'Dieser Plan belegt noch keine Racks. Beim Einplanen werden sie festgelegt.',
    en: 'This plan holds no racks yet. Scheduling it commits them.',
  },
  // ── the move dialog ────────────────────────────────────────────────────────
  //
  // ⚠ Every lifecycle move used to open a dialog TITLED with the resulting STATUS
  // ("Completed") and confirmed by a button reading `Save`. Both were wrong in the
  // same way: an operator is performing an ACTION, and "Save" says nothing about
  // which one — the same word on the button that schedules a crop, starts it,
  // finishes it and abandons it. User, 2026-09-07: *"save is so generic"*.
  //
  // Titles are the action in the imperative; the confirm button repeats the verb.
  // The dismiss button is `close`, never `cancel`, because on the Cancel-plan
  // dialog two buttons both reading "Cancel" is a genuine ambiguity.
  moveTitleScheduled: { de: 'Plan einplanen', en: 'Schedule this plan' },
  moveTitleActive: { de: 'Plan starten', en: 'Start this plan' },
  moveTitleCompleted: { de: 'Plan abschließen', en: 'Complete this plan' },
  moveTitleCancelled: { de: 'Plan stornieren', en: 'Cancel this plan' },
  moveTitleFailed: { de: 'Plan als gescheitert melden', en: 'Mark this plan as failed' },
  moveTitleDraft: { de: 'Zurück in den Entwurf', en: 'Return to draft' },
  returnToDraft: { de: 'In Entwurf zurücksetzen', en: 'Return to draft' },
  moveClose: { de: 'Schließen', en: 'Close' },

  // ── needs_force: the refusal an operator may override ──────────────────────
  forceTitle: { de: 'Trotzdem abschließen?', en: 'Complete it anyway?' },
  forceWhat: {
    de: 'Die {n} nicht erfassten Phasen werden als ÜBERSPRUNGEN vermerkt — nicht als erledigt. Es wird kein Enddatum erfunden.',
    en: 'The {n} unrecorded stage(s) will be marked SKIPPED — not done. No end date is invented for them.',
  },
  forceReasonHint: {
    de: 'Eine Kultur vorzeitig zu beenden ist eine Entscheidung, die jemand verantwortet — bitte begründen.',
    en: 'Cutting a crop short is a decision somebody owns — say why.',
  },
  forceConfirm: { de: 'Trotzdem abschließen', en: 'Force complete' },
  forceRecordInstead: { de: 'Phasen erfassen', en: 'Record the stages instead' },

  schedulePlan: { de: 'Einplanen', en: 'Schedule' },
  scheduleHint: {
    de: 'Belegt die Racks verbindlich und erzeugt die Aufgaben. Die Kapazität wird dabei erneut geprüft.',
    en: 'Commits the racks and creates the work. The capacity is re-checked as it happens.',
  },
  startPlan: { de: 'Starten', en: 'Start' },
  completePlan: { de: 'Abschließen', en: 'Complete' },
  cancelPlan: { de: 'Stornieren', en: 'Cancel' },
  failPlan: { de: 'Als gescheitert melden', en: 'Mark as failed' },
  reason: { de: 'Grund', en: 'Reason' },
  reasonRequired: {
    de: 'Eine Kultur aufzugeben ist eine Entscheidung, die jemand verantwortet — bitte begründen.',
    en: 'Abandoning a crop is a decision somebody owns — say why.',
  },
  versionConflict: {
    de: 'Jemand anderes hat diesen Plan inzwischen geändert. Bitte neu laden und erneut versuchen.',
    en: 'Somebody else changed this plan in the meantime. Reload it and try again.',
  },

  // ── the timeline ───────────────────────────────────────────────────────────
  timelineTitle: { de: 'Anbau-Zeitachse', en: 'Growing timeline' },
  timelineDesc: {
    de: 'Was wo wächst und wann ein Raum wieder frei wird.',
    en: 'What is growing where, and when a room frees up.',
  },
  byPlan: { de: 'Nach Plan', en: 'By plan' },
  byRoom: { de: 'Nach Raum', en: 'By room' },
  today: { de: 'Heute', en: 'Today' },
  blocked: { de: 'Gesperrt', en: 'Blocked' },
  inChangeover: { de: 'Reinigung', en: 'Cleaning' },
  nothingScheduled: { de: 'In diesem Zeitraum ist nichts geplant', en: 'Nothing is planned in this window' },

  // ── the backlog ────────────────────────────────────────────────────────────
  tasksTitle: { de: 'Aufgaben', en: 'Production tasks' },
  tasksDesc: {
    de: 'Die Arbeit, die ein eingeplanter Anbau erzeugt — plus alles, was von Hand ergänzt wird.',
    en: 'The work a scheduled plan creates, plus anything raised by hand.',
  },
  newTask: { de: 'Neue Aufgabe', en: 'New task' },
  taskTitle: { de: 'Titel', en: 'Title' },
  taskTitlePlaceholder: {
    de: 'z. B. Rack-Beleuchtung in GR07 prüfen',
    en: 'e.g. Inspect the rack lights in GR07',
  },
  taskNotesPlaceholder: {
    de: 'Was zu tun ist und was die nächste Person wissen sollte',
    en: 'What needs doing, and anything the next person should know',
  },
  kind: { de: 'Art', en: 'Kind' },
  priority: { de: 'Priorität', en: 'Priority' },
  assignedRole: { de: 'Rolle', en: 'Role' },
  assignee: { de: 'Zugewiesen an', en: 'Assignee' },
  dueDate: { de: 'Fällig', en: 'Due' },
  unassigned: { de: 'Nicht zugewiesen', en: 'Unassigned' },
  generated: { de: 'Automatisch', en: 'Generated' },
  generatedHint: {
    de: 'Aus dem Plan erzeugt. Stornieren statt löschen — sonst kommt sie beim nächsten Einplanen zurück.',
    en: 'Created from its plan. Cancel it rather than deleting — otherwise it comes back the next time the plan is scheduled.',
  },
  blockedReason: { de: 'Grund der Blockade', en: 'What is blocking it' },
  blockedRequired: {
    de: 'Eine blockierte Aufgabe ohne Grund kann niemand entsperren.',
    en: 'A blocked task with no reason is one nobody can unblock.',
  },
  comment: { de: 'Kommentar', en: 'Comment' },
  addComment: { de: 'Kommentieren', en: 'Add a comment' },
  history: { de: 'Verlauf', en: 'History' },
  historyCreated: { de: 'Erstellt', en: 'Created' },
  historyCreatedByPlan: { de: 'Vom Plan erstellt', en: 'Created by its plan' },
  noTasks: { de: 'Nichts zu tun', en: 'Nothing to do' },
  noTasksHint: {
    de: 'Aufgaben entstehen, sobald ein Plan eingeplant wird.',
    en: 'Tasks appear as soon as a plan is scheduled.',
  },

  kindSow: { de: 'Aussaat', en: 'Sow' },
  kindTransplant: { de: 'Umsetzen', en: 'Transplant' },
  kindHarvest: { de: 'Ernte', en: 'Harvest' },
  kindInspect: { de: 'Kontrolle', en: 'Inspect' },
  kindClean: { de: 'Reinigung', en: 'Clean' },
  kindDose: { de: 'Dosierung', en: 'Dose' },
  kindOther: { de: 'Sonstiges', en: 'Other' },

  taskBacklog: { de: 'Backlog', en: 'Backlog' },
  taskTodo: { de: 'Zu tun', en: 'To do' },
  taskInProgress: { de: 'In Arbeit', en: 'In progress' },
  taskBlocked: { de: 'Blockiert', en: 'Blocked' },
  taskDone: { de: 'Erledigt', en: 'Done' },
  taskCancelled: { de: 'Storniert', en: 'Cancelled' },

  priorityLow: { de: 'Niedrig', en: 'Low' },
  priorityNormal: { de: 'Normal', en: 'Normal' },
  priorityHigh: { de: 'Hoch', en: 'High' },
  priorityUrgent: { de: 'Dringend', en: 'Urgent' },

  // ── R-324 ──────────────────────────────────────────────────────────────────
  // The geometry spelled out. Three bare numbers (`16 × 8 × 4`) tell an operator
  // nothing about which is which, and the card is the one place the multiplication
  // is not visible in a form next to its labels.
  geometryLine: {
    de: '{racks} Racks × {trays} Trays × {subtrays} Sub-Trays',
    en: '{racks} racks × {trays} trays × {subtrays} sub-trays',
  },
  // Replaces a concatenation that rendered "… racks · racks · peak, next 8 weeks".
  racksUsedPeak: {
    de: '{used} von {total} Racks belegt · Spitze der nächsten 8 Wochen',
    en: '{used} of {total} racks used · peak over the next 8 weeks',
  },
  // The disagreement, with BOTH numbers and what to do about it — a badge that
  // only says "differs" makes an operator go looking for the other number.
  disagreesDetail: {
    de: 'Planung nutzt {configured}; der Standort meldet {observed}.',
    en: 'Planning uses {configured}; the site reports {observed}.',
  },
  removeConfig: { de: 'Konfiguration entfernen', en: 'Remove configuration' },
  removeConfigConfirm: {
    de: 'Rack-Geometrie für {room} entfernen? Der Raum ist danach nicht mehr planbar.',
    en: 'Remove the rack geometry for {room}? The room stops being plannable until it is configured again.',
  },
  // D4 — a refusal is an answer, and gets an answer's weight.
  refused: { de: 'Nicht möglich', en: 'Cannot do that' },
  // Advisory: plan-level, never blocking (backend pitfall #79) — a warning, not
  // a refusal, so it gets its own weight rather than borrowing the danger box.
  advisory: { de: 'Hinweis', en: 'Advisory' },
  // D3 — the wizard's two steps, named short enough for a stepper chip.
  wizardStep1Short: { de: 'Was und wie viel', en: 'What and how much' },
  wizardStep2Short: { de: 'Wohin', en: 'Where' },
  next: { de: 'Weiter', en: 'Next' },
  back: { de: 'Zurück', en: 'Back' },
  step1Hint: {
    de: 'Kultur, Rezeptversion, Zielmenge und Startdatum wählen — danach rechnet der Server Sub-Trays, Trays und Racks aus.',
    en: 'Pick a crop, a recipe version, a target weight and a start date. The server works out the sub-trays, trays and racks from there.',
  },
  // D5 — a phase may be carried by more than one room.
  useThisRoom: { de: 'Diesen Raum wählen', en: 'Use this room' },
  roomChosen: { de: 'Gewählt', en: 'Chosen' },
  recommenderStands: {
    de: 'Keine Auswahl — es gilt die Empfehlung oben.',
    en: 'Nothing chosen, so the recommendation above stands. Pick one or more rooms to overrule it.',
  },
  // R-324 round 6 — the room picker as cards, searchable.
  roomsHeading: { de: '{n} Räume', en: '{n} rooms' },
  searchRoomsHint: { de: 'Raum oder Art…', en: 'Room or kind…' },
  chosenList: { de: 'Gewählt: {rooms}', en: 'Chosen: {rooms}' },
  // Said BEFORE the room is picked, not after, in the split warning.
  takesWholePhase: {
    de: 'Nimmt die ganze Phase auf — {needed} Racks',
    en: 'Takes the whole phase — {needed} racks',
  },
  takesPartOfPhase: {
    de: 'Nimmt nur einen Teil auf — {free} der {needed} Racks',
    en: 'Takes part of it — {free} of the {needed} racks',
  },
  roomsRefused: {
    de: '{n} Räume können diese Phase nicht aufnehmen',
    en: '{n} rooms cannot take this phase',
  },
  noRoomMatches: { de: 'Kein Raum passt zu „{q}“', en: 'No room matches “{q}”' },
  noUsableRooms: {
    de: 'Kein Raum kann diese Phase aufnehmen.',
    en: 'No room can take this phase.',
  },
  // D10 — editing a plan.
  editPlan: { de: 'Plan bearbeiten', en: 'Edit plan' },
  editPlanHint: {
    de: 'Zielmenge, Start und Raumbelegung ändern. Wird abgelehnt, wenn jemand anderes den Plan inzwischen geändert hat.',
    en: 'Change the target, the start date or which rooms carry the work. Refused if somebody else has changed the plan since you opened it.',
  },
  saveChanges: { de: 'Änderungen speichern', en: 'Save changes' },
  // PLN-3 — reaching one plan among many.
  search: { de: 'Suche', en: 'Search' },
  searchPlansHint: { de: 'Plan, Kultur oder Raum…', en: 'Plan, crop or room…' },
  noMatches: { de: 'Nichts gefunden', en: 'Nothing matches' },
  noMatchesHint: {
    de: 'Kein Plan passt zu Suche und Statusfilter. Filter zurücksetzen, um alle zu sehen.',
    en: 'No plan matches that search and status filter. Clear them to see everything again.',
  },
  // TML-1 / TML-4 — the timeline window.
  windowStarts: { de: 'Zeitraum ab', en: 'Window starts' },
  jumpToToday: { de: 'Zu heute', en: 'Jump to today' },
  fitToPlans: { de: 'Alle Pläne zeigen', en: 'Show them all' },
  // TSK-1 / TSK-4 / TSK-5 — the board.
  searchTasksHint: { de: 'Aufgabe, Plan oder Raum…', en: 'Task, plan or room…' },
  taskKind: { de: 'Art', en: 'Kind' },
  columnEmpty: { de: 'Nichts hier', en: 'Nothing here' },
  laneFinal: { de: 'endgültig', en: 'final' },
  laneMore: { de: '{count} weitere – zum Laden scrollen', en: '{count} more — scroll to load' },
  dropHere: { de: 'Hier ablegen: {status}', en: 'Drop here to move to {status}' },
  blockTitle: { de: '{code} blockieren?', en: 'Block {code}?' },
  blockMsg: {
    de: 'Eine blockierte Aufgabe braucht einen Grund, damit die nächste Schicht weiß, was sie aufhält.',
    en: 'A blocked task needs a reason, so the next shift knows what is holding it up.',
  },
  blockReasonPlaceholder: {
    de: 'z. B. Saatgut für das GR07-Lager noch nicht geliefert',
    en: 'e.g. Waiting for seed delivery to the GR07 store',
  },
  blockConfirm: { de: 'Aufgabe blockieren', en: 'Block task' },
  deleteTask: { de: 'Aufgabe löschen', en: 'Delete task' },
  deleteTaskConfirm: {
    de: '{code} endgültig löschen? Nur von Hand angelegte Aufgaben lassen sich löschen.',
    en: 'Delete {code} for good? Only a task somebody raised by hand can be deleted — a generated one is cancelled and kept.',
  },
  allRoles: { de: 'Alle Rollen', en: 'All roles' },
  allKinds: { de: 'Alle Arten', en: 'All kinds' },
  allPlans: { de: 'Alle Pläne', en: 'All plans' },
  dragHint: { de: 'Karten per Drag & Drop verschieben', en: 'Drag a card to move it' },
  when: { de: 'Wann', en: 'When' },
  createdAction: { de: 'Erstellt', en: 'Created' },
  whatHappened: { de: 'Vorgang', en: 'What happened' },
  who: { de: 'Wer', en: 'Who' },
  currentStage: { de: 'Aktuelle Phase', en: 'Now' },
  // R-324 — the rack map.
  pickRacks: { de: 'Racks wählen', en: 'Pick the racks' },
  pickRacksHint: {
    de: 'Frei für den ganzen Zeitraum dieser Phase. Belegte und in Reinigung befindliche Racks sind gesperrt. Ohne Auswahl wählt der Server zusammenhängende freie Reihen.',
    en: 'Free for this whole phase. Racks held by another plan, or still in changeover, cannot be picked. Choose none and the server takes a contiguous free block.',
  },
  rackChosen: { de: '{n} von {needed} gewählt', en: '{n} of {needed} chosen' },
  rackTakenLegend: { de: 'Belegt', en: 'Held by another plan' },
  rackCleaning: { de: 'In Reinigung', en: 'In changeover' },
  // R-354 — a booked row the room does not actually have. Written as a place,
  // not as an error: the plan is legitimate, the shelf is the thing that is gone.
  rackBeyond: { de: 'Über die Racks des Raums hinaus', en: 'Beyond the room’s racks' },
  // R-324 — where rooms actually come from.
  roomsComeFromTree: {
    de: 'Räume entstehen im Standortbaum: eine Zone als Aussaat-, Keim- oder Grow-Raum markieren, dann wird sie hier planbar. Hier wird nur die Rack-Geometrie gesetzt; Entfernen steckt im Dialog des Raums.',
    en: 'Rooms are created in the location tree — tag a zone as a seeding, germination or grow room and it appears here. This screen sets the rack geometry only; removing a room’s configuration is inside its own dialog.',
  },
  openLocations: { de: 'Standortbaum öffnen', en: 'Open Locations' },

  // ── R-354: the location tree is the ONE source of racks ────────────────────
  // A rack is a node in the tree, so the rack COUNT is the site's fact and this
  // screen only mirrors it. The wording never says "configured" about a number
  // the operator cannot change here — it names the site and points at the screen
  // where racks are actually added.
  racksFromSite: { de: '{n} · vom Standort', en: '{n} · from the site' },
  racksSourceSite: { de: 'Racks: vom Standort', en: 'Racks: from the site' },
  racksSourceConfigured: { de: 'Racks: hier eingetragen', en: 'Racks: set here' },
  openRackLevels: { de: 'Racks & Ebenen in Standorte', en: 'Racks & levels in Locations' },
  racksFromSiteHint: {
    de: 'Racks sind Knoten im Standortbaum. Diese Zahl spiegelt, was der Standort meldet, und wird hier nicht bearbeitet — Racks werden unter Standorte → Racks & Ebenen angelegt.',
    en: 'Racks are nodes in the location tree. This number mirrors what the site reports and is not edited here — racks are added under Locations → Racks & levels.',
  },
  racksUnwiredHint: {
    de: 'Der Standort hat für diesen Raum noch keine Racks gemeldet — bis dahin gilt diese Zahl.',
    en: 'The site has not reported racks yet — this number is used until it does.',
  },
  // The trays half of the disagreement. Once racks come FROM the site they
  // cannot disagree WITH it, so that sentence would contradict itself.
  disagreesTraysDetail: {
    de: 'Planung nutzt {configured} Trays je Rack; der Standort meldet {observed}.',
    en: 'Planning uses {configured} trays per rack; the site reports {observed}.',
  },

  // The drift banner. It appears only when there is drift — a banner that is
  // always on screen is a banner nobody reads.
  driftTitle_one: {
    de: 'Ein Plan belegt mehr Racks, als der Standort hat',
    en: 'One plan books more racks than the site has',
  },
  driftTitle_many: {
    de: '{n} Pläne belegen mehr Racks, als der Standort hat',
    en: '{n} plans book more racks than the site has',
  },
  driftRoomsOnly_one: {
    de: 'Ein Raum weicht in der Rack-Zahl vom Standort ab',
    en: 'One room’s rack count disagrees with the site',
  },
  driftRoomsOnly_many: {
    de: '{n} Räume weichen in der Rack-Zahl vom Standort ab',
    en: '{n} rooms’ rack counts disagree with the site',
  },
  driftHint: {
    de: 'Laufende Pläne werden nicht angetastet. Entweder Racks unter Standorte → Racks & Ebenen ergänzen oder den Plan kleiner planen — sonst beleuchtet der Standort jedes Rack des Raums für diese Kultur.',
    en: 'Nothing is cancelled and no running plan is shrunk. Either add racks under Locations → Racks & levels, or size the plan down — otherwise the site lights every rack in the room for this crop.',
  },
  driftSiteRacks: { de: 'Racks am Standort', en: 'Racks on site' },
  driftConfigured: { de: 'Planung nutzt', en: 'Planning uses' },
  driftBooked: { de: 'Belegte Racks', en: 'Racks booked' },
  driftNoPlan: { de: 'Kein Plan belegt diesen Raum', en: 'No plan books this room' },
  // The other plans in a drifted room. They are listed because re-planning the
  // room moves them too — but they are not the problem, and must not look like it.
  driftFits: { de: 'Passt', en: 'Fits' },
  driftUnknown: { de: 'Nicht gemeldet', en: 'Not reported' },
  planNotFound: { de: 'Plan nicht gefunden', en: 'Plan not found' },
  planNotFoundHint: {
    de: 'Kein Plan mit der Kennung „{id}". Er wurde vielleicht gelöscht, oder der Link ist falsch.',
    en: 'No plan matches “{id}”. It may have been removed, or the link may be wrong.',
  },

  // ── crop-cycle alerts (R-353) ──────────────────────────────────────────────
  // The Production → Alerts page. DE drafted — native-review queue.
  //
  // "Warnung" and not "Alarm": an ALARM in this console is the SOC's, raised on a
  // device, and a crop alert is a different object with a different owner. Two
  // German words for two English ones, kept apart on purpose.
  alertsTitle: { de: 'Warnungen', en: 'Alerts' },
  alertsDesc: {
    de: 'Was die Kulturen dieses Standorts gemeldet haben — Klima, Störungen und der Zyklus selbst.',
    en: 'What this site’s crops reported — climate, incidents, and the cycle itself.',
  },
  alertsCriticalOpen: { de: 'Kritisch · offen', en: 'Critical · open' },
  alertsWarningOpen: { de: 'Warnung · offen', en: 'Warning · open' },
  alertsLast24h: { de: 'Letzte 24 Stunden', en: 'Last 24 hours' },
  alertsByPlan: { de: 'Nach Plan', en: 'By plan' },
  alertsUnacked: { de: 'Nicht bestätigt', en: 'Unacknowledged' },
  alertsEmpty: { de: 'Keine Meldungen', en: 'Nothing reported' },
  alertsEmptyHint: {
    de: 'Dieser Standort hat in den letzten 30 Tagen nichts gemeldet. Das ist die gute Nachricht.',
    en: 'This site has reported nothing in the last 30 days. That is the good news.',
  },
  clearFilters: { de: 'Filter zurücksetzen', en: 'Clear filters' },
  loadMore: { de: 'Mehr laden', en: 'Load more' },
  allRooms: { de: 'Alle Räume', en: 'All rooms' },
  plan: { de: 'Plan', en: 'Plan' },
  alertKind: { de: 'Art der Meldung', en: 'Alert kind' },
  reading: { de: 'Messwert', en: 'Reading' },
  acknowledge: { de: 'Bestätigen', en: 'Acknowledge' },
  acknowledged: { de: 'Bestätigt', en: 'Acknowledged' },
  alertAckedToast: { de: 'Meldung bestätigt', en: 'Alert acknowledged' },
  ackConfirmTitle: { de: 'Meldung bestätigen?', en: 'Acknowledge this alert?' },
  ackConfirmMessage: {
    de: '„{title}“ wird für den ganzen Standort als bestätigt markiert. Das lässt sich nicht rückgängig machen.',
    en: '“{title}” will be marked acknowledged for the whole site. This cannot be undone.',
  },
  notAcked: { de: 'Offen', en: 'Open' },

  // The severity FILTER is a minimum, exactly as the backend reads it — the
  // labels have to say so, or the criticals inside a "Warning" list read as a bug.
  severity: { de: 'Stufe', en: 'Severity' },
  allSeverities: { de: 'Alle Stufen', en: 'All severities' },
  sevInfoUp: { de: 'Info und höher', en: 'Info and up' },
  sevWarningUp: { de: 'Warnung und höher', en: 'Warning and up' },
  sevCriticalOnly: { de: 'Nur kritisch', en: 'Critical only' },
  // A ROW's own severity, spelled out — the coloured dot alone says nothing to a
  // screen reader or to a colour-blind operator.
  sevInfo: { de: 'Info', en: 'Info' },
  sevWarning: { de: 'Warnung', en: 'Warning' },
  sevCritical: { de: 'Kritisch', en: 'Critical' },

  // Metadata keys shown in an expanded row (design §1's closed list).
  band: { de: 'Bereich', en: 'Band' },
  condition: { de: 'Bedingung', en: 'Condition' },
  stageCode: { de: 'Phase', en: 'Stage' },
  escalationLevel: { de: 'Eskalationsstufe', en: 'Escalation level' },
  daysLeft: { de: 'Tage verbleibend', en: 'Days left' },
  plannedEnd: { de: 'Geplantes Ende', en: 'Planned end' },
  catalogueCode: { de: 'Katalog-Code', en: 'Catalogue code' },

  // How a reading's zone READS. "Über dem Bereich" rather than "orange hoch":
  // orange is the backend's name for the band, not something an operator acts on.
  zoneCriticalLow: { de: 'Kritisch niedrig', en: 'Critically low' },
  zoneOrangeLow: { de: 'Unter dem Bereich', en: 'Below range' },
  zoneOrangeHigh: { de: 'Über dem Bereich', en: 'Above range' },
  zoneCriticalHigh: { de: 'Kritisch hoch', en: 'Critically high' },
  zoneOptimal: { de: 'Im Optimum', en: 'In range' },

  // The 15 `cultivation.*` kinds (design §1). Written as what HAPPENED, in the
  // operator's words — never the dotted identifier, which stays visible in the
  // expanded row for anyone who needs to match it against a log.
  alertZone: { de: 'Klimabereich verlassen', en: 'Climate out of range' },
  alertZoneRecovered: { de: 'Klima wieder im Bereich', en: 'Climate back in range' },
  alertIncidentEscalated: { de: 'Störung eskaliert', en: 'Incident escalated' },
  alertIncidentFallback: { de: 'Kulturschutz ausgelöst', en: 'Crop protection fired' },
  alertIncidentResolved: { de: 'Störung behoben', en: 'Incident resolved' },
  alertRoomUnmonitored: { de: 'Raum nicht überwacht', en: 'Room unmonitored' },
  alertRoomUnplanned: { de: 'Raum ohne Tagesplan', en: 'Room has no day plan' },
  alertCycleStarted: { de: 'Zyklus gestartet', en: 'Cycle started' },
  alertStageChanged: { de: 'Phase erfasst', en: 'Stage recorded' },
  alertStageOverdue: { de: 'Phase überfällig', en: 'Stage overdue' },
  alertHarvestDue: { de: 'Ernte steht an', en: 'Harvest due' },
  alertHarvestOverdue: { de: 'Ernte überfällig', en: 'Harvest overdue' },
  alertCycleCompleted: { de: 'Zyklus abgeschlossen', en: 'Cycle completed' },
  alertCycleFailed: { de: 'Zyklus gescheitert', en: 'Cycle failed' },
  alertCycleCancelled: { de: 'Zyklus abgebrochen', en: 'Cycle cancelled' },
  // R-354. The plan books more racks in a room than the room has, so the site
  // cannot tell the racks apart and lights ALL of them for this crop. Named
  // after what it DOES on the floor, not after the counter that disagreed.
  alertRackMismatch: { de: 'Racks passen nicht zum Plan', en: 'Racks do not match the plan' },
  alertExecutorLegacy: { de: 'Bridge ignoriert Rack-Adressen', en: 'Bridge ignores rack addresses' },
  alertRackBoardSilent: {
    de: 'Rack-Controller meldet sich nicht',
    en: 'Rack controller has never announced',
  },
  alertBandsConflict: { de: 'Rezepte im Raum widersprechen sich', en: 'Recipes in the room conflict' },
  // R-369. A stage is watered (`ebb_flood`/`drip`/`plain_water`) but none of its
  // blocks state a duration × repeat, so `drive_loop` drives nothing for it —
  // daily, until an operator adds a run. Used to be a `warn` log only.
  alertLoopUnirrigated: { de: 'Kein Bewässerungslauf im Rezept', en: 'Recipe states no watering run' },

  // Feedback for writes that used to close a dialog and say nothing, and confirms
  // for the task moves that cannot be taken back (`done` and `cancelled` are terminal).
  taskDoneConfirmTitle: { de: '{code} als erledigt markieren?', en: 'Mark {code} done?' },
  taskDoneConfirmMsg: {
    de: 'Eine erledigte Aufgabe lässt sich nicht wieder öffnen — sie hält fest, wann die Arbeit getan wurde.',
    en: 'A finished task cannot be reopened — it is the record of when the work was done.',
  },
  taskDoneConfirm: { de: 'Als erledigt markieren', en: 'Mark done' },
  taskCancelConfirmTitle: { de: '{code} stornieren?', en: 'Cancel {code}?' },
  taskCancelConfirmMsg: {
    de: 'Eine stornierte Aufgabe lässt sich nicht wieder öffnen. Sie bleibt als Nachweis beim Plan.',
    en: 'A cancelled task cannot be reopened. It stays on its plan as the record of work that was dropped.',
  },
  taskCancelConfirm: { de: 'Aufgabe stornieren', en: 'Cancel task' },
  taskMovedToast: { de: '{code}: Status jetzt „{status}“', en: '{code} is now {status}' },
  taskDeletedToast: { de: 'Aufgabe {code} gelöscht', en: 'Task {code} deleted' },
  taskSavedToast: { de: 'Aufgabe gespeichert', en: 'Task saved' },
  roomCapacitySavedToast: { de: 'Kapazität für {room} gespeichert', en: 'Capacity for {room} saved' },
  roomConfigRemovedToast: { de: 'Konfiguration für {room} entfernt', en: 'Configuration for {room} removed' },
  yieldSavedToast: { de: 'Ertrag gespeichert', en: 'Yield saved' },
  yieldDeleteTitle: { de: 'Ertrag für {crop} löschen?', en: 'Delete the yield for {crop}?' },
  yieldDeleteMsg: {
    de: '{min}–{max} g je Sub-Tray werden aus der Ertragstabelle entfernt. Ohne Ertrag lässt sich die Kultur nicht planen.',
    en: '{min}–{max} g per sub-tray is removed from the yield table. A crop with no yield cannot be planned.',
  },
  yieldDeletedToast: { de: 'Ertrag für {crop} gelöscht', en: 'Yield for {crop} deleted' },
  planUpdatedToast: { de: 'Plan {code} aktualisiert', en: 'Plan {code} updated' },
  planMovedToast: { de: 'Plan-Status jetzt „{status}“', en: 'Plan is now {status}' },
} satisfies Namespace;
